import { useState } from 'react';

// Ported verbatim from RealityOperator (frontend/src/components/LanguageFlag.tsx)
// so the desk and the operator floor show the same flags. Keep the two in sync
// when a language is added.
//
// Languages we ship an inline-SVG flag for. Anything else renders no flag (the
// text code / name still shows). Emoji flags are intentionally NOT used: on
// Windows (incl. the Electron kiosk) they render as plain letter pairs, not
// flags. English maps to the UK flag (common in EU tourism) — swap to US below
// if preferred.
const SUPPORTED_FLAG_LANGS = new Set([
  'nl', 'en', 'de', 'fr', 'it', 'es', 'ja', 'ro', 'ru', 'zh', 'tr', 'pl',
]);

export function hasLanguageFlag(code?: string | null): boolean {
  return SUPPORTED_FLAG_LANGS.has((code || '').toLowerCase());
}

// Unit 5-pointed star: outer radius 1, centred on the origin, pointing up.
// Placed via transform translate/scale (no ids, so multiple instances are safe).
const STAR =
  'M0,-1 L0.2245,-0.309 0.9511,-0.309 0.3633,0.118 0.5878,0.809 0,0.382 -0.5878,0.809 -0.3633,0.118 -0.9511,-0.309 -0.2245,-0.309 Z';

// Module counter -> a stable unique id per Union Jack instance (its clipPaths
// must be unique). Captured once via useState so it survives re-renders.
let flagSeq = 0;

interface LanguageFlagProps {
  code?: string | null;
  className?: string;
}

export default function LanguageFlag({ code, className }: LanguageFlagProps) {
  const [uid] = useState(() => `uk${flagSeq++}`);
  const c = (code || '').toLowerCase();
  const cls = `lang-flag-svg${className ? ` ${className}` : ''}`;
  const common = { className: cls, 'aria-hidden': true as const };

  switch (c) {
    case 'nl':
      return (
        <svg viewBox="0 0 9 6" {...common}>
          <rect width="9" height="2" fill="#AE1C28" />
          <rect y="2" width="9" height="2" fill="#fff" />
          <rect y="4" width="9" height="2" fill="#21468B" />
        </svg>
      );
    case 'de':
      return (
        <svg viewBox="0 0 5 3" {...common}>
          <rect width="5" height="1" fill="#000" />
          <rect y="1" width="5" height="1" fill="#D00" />
          <rect y="2" width="5" height="1" fill="#FFCE00" />
        </svg>
      );
    case 'fr':
      return (
        <svg viewBox="0 0 3 2" {...common}>
          <rect width="1" height="2" fill="#0055A4" />
          <rect x="1" width="1" height="2" fill="#fff" />
          <rect x="2" width="1" height="2" fill="#EF4135" />
        </svg>
      );
    case 'it':
      return (
        <svg viewBox="0 0 3 2" {...common}>
          <rect width="1" height="2" fill="#009246" />
          <rect x="1" width="1" height="2" fill="#fff" />
          <rect x="2" width="1" height="2" fill="#CE2B37" />
        </svg>
      );
    case 'es':
      return (
        <svg viewBox="0 0 3 2" {...common}>
          <rect width="3" height="2" fill="#AA151B" />
          <rect y="0.5" width="3" height="1" fill="#F1BF00" />
        </svg>
      );
    case 'en': {
      const a = `${uid}a`;
      const b = `${uid}b`;
      return (
        <svg viewBox="0 0 60 30" {...common}>
          <clipPath id={a}>
            <path d="M0,0 v30 h60 v-30 z" />
          </clipPath>
          <clipPath id={b}>
            <path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z" />
          </clipPath>
          <g clipPath={`url(#${a})`}>
            <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
            <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
            <path d="M0,0 L60,30 M60,0 L0,30" clipPath={`url(#${b})`} stroke="#C8102E" strokeWidth="4" />
            <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
            <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
          </g>
        </svg>
      );
    }
    case 'ja':
      return (
        <svg viewBox="0 0 9 6" {...common}>
          <rect width="9" height="6" fill="#fff" />
          <circle cx="4.5" cy="3" r="1.8" fill="#BC002D" />
        </svg>
      );
    case 'ro':
      return (
        <svg viewBox="0 0 3 2" {...common}>
          <rect width="1" height="2" fill="#002B7F" />
          <rect x="1" width="1" height="2" fill="#FCD116" />
          <rect x="2" width="1" height="2" fill="#CE1126" />
        </svg>
      );
    case 'ru':
      return (
        <svg viewBox="0 0 9 6" {...common}>
          <rect width="9" height="2" fill="#fff" />
          <rect y="2" width="9" height="2" fill="#0039A6" />
          <rect y="4" width="9" height="2" fill="#D52B1E" />
        </svg>
      );
    case 'pl':
      return (
        <svg viewBox="0 0 8 5" {...common}>
          <rect width="8" height="5" fill="#fff" />
          <rect y="2.5" width="8" height="2.5" fill="#DC143C" />
        </svg>
      );
    case 'zh':
      return (
        <svg viewBox="0 0 30 20" {...common}>
          <rect width="30" height="20" fill="#DE2910" />
          <path d={STAR} fill="#FFDE00" transform="translate(5,5) scale(3)" />
          <path d={STAR} fill="#FFDE00" transform="translate(10,2) scale(1)" />
          <path d={STAR} fill="#FFDE00" transform="translate(12,4) scale(1)" />
          <path d={STAR} fill="#FFDE00" transform="translate(12,7) scale(1)" />
          <path d={STAR} fill="#FFDE00" transform="translate(10,9) scale(1)" />
        </svg>
      );
    case 'tr':
      return (
        <svg viewBox="0 0 30 20" {...common}>
          <rect width="30" height="20" fill="#E30A17" />
          <circle cx="11.5" cy="10" r="5" fill="#fff" />
          <circle cx="13" cy="10" r="4" fill="#E30A17" />
          <path d={STAR} fill="#fff" transform="translate(17.5,10) scale(2)" />
        </svg>
      );
    default:
      return null;
  }
}

// Chip content: flag art plus the bare 2-letter code. Used by every play-language
// chip and badge so the desk, the queue row and the operator floor agree.
export function LanguageChipLabel({ code }: { code?: string | null }) {
  const c = (code || '').toLowerCase();
  if (!c) return <>?</>;
  return (
    <>
      {hasLanguageFlag(c) ? <LanguageFlag code={c} className="lang-chip-flag" /> : null}
      <span className="lang-chip-code">{c.toUpperCase()}</span>
    </>
  );
}
